<!-- src/views/WageSummaryView.vue -->
<template>
  <div class="section wage-summary-view">
    <!-- Toolbar (same UI as rota, but read-only) -->
    <RotaToolbar
      :week-range-text="displayRangeText"
      :can-go-next="canGoNext"
      :can-edit="false"
      :busy="loading || loadingStaff"
      :show-date-range-picker="true"
      :range-start="overrideStartDate"
      :range-end="overrideEndDate"
      @prev="onPrev"
      @next="onNext"
      @refresh="reload"
      @date-range-change="applyDateRangeOverride"
      @date-range-clear="clearDateRangeOverride"
    />

    <div class="card wage-summary-card">
      <div class="card-content">
        <div class="wage-summary-content-wrap">
          <WageSummaryContent
            :venue-id="venueId || ''"
            :week-days="displayWeekDays"
            :week-range-text="displayRangeText"
            :week-ending-date="displayWeekEndingDate"
            :staff-rows="staff"
            :shifts-for-cell="shiftsForCell"
            :is-operations="isOperations"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onUnmounted } from "vue";

// ✅ IMPORTANT: adjust import path to your actual file location
// If your file is at src/components/wages/WageSummaryContent.vue, this must be:
import WageSummaryContent from "@/components/WageSummaryContent.vue";

import RotaToolbar from "@/components/rota/RotaToolbar.vue";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";
import useRota from "@/scripts/useScripts/useRota";
import { callEdge } from "@/scripts/sqlApiHelper/edgeApi";

import { auth, db } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

const { venueId } = useVenueContext();

/**
 * ONE rota instance, owned by this view.
 */
const {
  loading,
  weekDays,
  weekRangeText,
  canGoNext,
  prevWeek,
  nextWeek,
  staff,
  shiftsForCell,
  loadWeek,
  loadDateRange,
} = useRota();

/**
 * Temporary date range override.
 *
 * Empty = normal single-week mode.
 * Populated = multi-week / custom date range mode.
 */
const overrideStartDate = ref("");
const overrideEndDate = ref("");

const hasDateRangeOverride = computed(() => {
  return Boolean(overrideStartDate.value && overrideEndDate.value);
});

/**
 * Days passed into WageSummaryContent.
 *
 * Normal mode:
 * - useRota weekDays.
 *
 * Override mode:
 * - all days between selected From date and End date.
 */
const displayWeekDays = computed(() => {
  if (!hasDateRangeOverride.value) {
    return Array.isArray(weekDays.value) ? weekDays.value : [];
  }

  return buildDateRangeDays(overrideStartDate.value, overrideEndDate.value);
});

/**
 * Range text displayed in toolbar and wage summary.
 */
const displayRangeText = computed(() => {
  if (!hasDateRangeOverride.value) {
    return weekRangeText.value || "";
  }

  return `${formatDateDMY(overrideStartDate.value)} → ${formatDateDMY(
    overrideEndDate.value
  )}`;
});

/**
 * Week ending date passed into WageSummaryContent.
 *
 * In override mode this becomes the custom End date.
 */
const displayWeekEndingDate = computed(() => {
  if (hasDateRangeOverride.value) {
    return overrideEndDate.value;
  }

  const days = Array.isArray(weekDays.value) ? weekDays.value : [];
  if (!days.length) return "";

  const last = days[6] || days[days.length - 1];
  return String(last?.ymd || "").trim();
});

/** ✅ ops flag aligned to NavBar.vue (Firestore users/{uid}) */
const isOperations = ref(false);
let unsubscribeAuth = null;

async function loadOpsFlagForUid(uid) {
  isOperations.value = false;
  if (!uid) return;

  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) return;

    const d = snap.data() || {};
    isOperations.value =
      d.isOperations === true || d.is_operations === true || d.operations === true;
  } catch (e) {
    console.warn("[WAGE VIEW][OPS] load error:", e);
    isOperations.value = false;
  }
}

unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
  if (!user) {
    isOperations.value = false;
    return;
  }

  await loadOpsFlagForUid(user.uid);
});

onUnmounted(() => {
  if (unsubscribeAuth) unsubscribeAuth();
});

/** load staff pay rates from Supabase (needed for wage calcs) */
const STAFF_DETAILS_LIST = "staff-details-list";
const loadingStaff = ref(false);

async function loadStaff() {
  const v = String(venueId.value || "").trim();
  if (!v) return;

  loadingStaff.value = true;

  try {
    const res = await callEdge(STAFF_DETAILS_LIST, { venue_id: v });
    const list = Array.isArray(res?.staff) ? res.staff : [];
    staff.value = list;
  } catch (e) {
    console.warn("[WAGE VIEW][STAFF] load error:", e);
    staff.value = [];
  } finally {
    loadingStaff.value = false;
  }
}

/**
 * Unified reload.
 *
 * Normal mode:
 * - loads the selected rota week.
 *
 * Override mode:
 * - loads all rota shifts between selected From date and End date.
 */
async function reload() {
  if (!venueId.value) return;

  if (hasDateRangeOverride.value) {
    await loadDateRange(venueId.value, overrideStartDate.value, overrideEndDate.value);
  } else {
    await loadWeek(venueId.value);
  }

  // Ensure staff master list with pay rates is current
  await loadStaff();

  const day0 = displayWeekDays.value?.[0]?.ymd;
  const sampleStaffId = staff.value?.[0]?.id;
  const sampleShiftCount =
    sampleStaffId && day0 ? (shiftsForCell(sampleStaffId, day0) || []).length : 0;

  // console.log("[WAGE VIEW] reload complete:", {
  //   overrideActive: hasDateRangeOverride.value,
  //   overrideStartDate: overrideStartDate.value,
  //   overrideEndDate: overrideEndDate.value,
  //   displayWeekEndingDate: displayWeekEndingDate.value,
  //   days: displayWeekDays.value?.length || 0,
  //   staff: staff.value?.length || 0,
  //   isOperations: isOperations.value,
  //   sample: { day0, sampleStaffId, sampleShiftCount },
  // });
}

async function onPrev() {
  if (hasDateRangeOverride.value) return;

  prevWeek();
  await nextTick();
  await reload();
}

async function onNext() {
  if (hasDateRangeOverride.value) return;

  nextWeek();
  await nextTick();
  await reload();
}

async function applyDateRangeOverride({ startDate, endDate }) {
  const cleanStart = String(startDate || "").trim();
  const cleanEnd = String(endDate || "").trim();

  if (!cleanStart || !cleanEnd) return;
  if (cleanStart > cleanEnd) return;

  overrideStartDate.value = cleanStart;
  overrideEndDate.value = cleanEnd;

  await nextTick();
  await reload();
}

async function clearDateRangeOverride() {
  overrideStartDate.value = "";
  overrideEndDate.value = "";

  await nextTick();
  await reload();
}

/** initial load when venue becomes available */
watch(
  venueId,
  async (v) => {
    if (!v) return;
    await reload();
  },
  { immediate: true }
);

/**
 * Build every day between two yyyy-mm-dd dates.
 */
function buildDateRangeDays(startISO, endISO) {
  const start = parseISODateLocal(startISO);
  const end = parseISODateLocal(endISO);

  if (!start || !end) return [];
  if (start > end) return [];

  const days = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    const ymd = toISODate(cursor);

    days.push({
      ymd,
      date: new Date(cursor),
      dayName: cursor.toLocaleDateString("en-GB", { weekday: "long" }),
      dayShort: cursor.toLocaleDateString("en-GB", { weekday: "short" }),
      label: cursor.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
      }),
    });

    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

function parseISODateLocal(iso) {
  if (!iso) return null;

  const parts = String(iso).split("-");
  if (parts.length !== 3) return null;

  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function toISODate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatDateDMY(iso) {
  if (!iso) return "";

  const parts = String(iso).split("-");
  if (parts.length !== 3) return iso;

  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}
</script>

<style scoped>
.wage-summary-view {
  width: 100%;
}

.wage-summary-card {
  border-radius: 12px;
}

.wage-summary-content-wrap {
  width: 100%;
}

@media (max-width: 600px) {
  .wage-summary-card .card-content {
    padding: 10px;
  }

  .wage-summary-content-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .wage-summary-content-wrap :deep(table) {
    min-width: 760px;
  }
}
</style>
